import { ReportRequest } from '@/client/reports/types/reports.type';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin/ChartEmptyState';
import { useProductActivityOverTimeReports } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { ActivityBarChart } from '@/ui/charts/bar-chart/ActivityBarChart';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ChartWrapper = styled.div`
  height: 100%;
  min-width: 0;
  max-width: 100%;
`;

type ProductActivityOverTimeProps = {
  filters: ReportRequest;
  activeAwarenessTraining?: boolean;
  activePhishing?: boolean;
  activeSecurityCulture?: boolean;
  isMainDashboard?: boolean;
};

export const ProductActivityOverTime = ({
  filters,
  activeAwarenessTraining = false,
  activePhishing = false,
  activeSecurityCulture = false,
  isMainDashboard = false,
}: ProductActivityOverTimeProps) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { productActivityOverTime, isLoading } =
    useProductActivityOverTimeReports(currentAccount?.id as string, filters);

  const hasNoProductActivityData =
    !isLoading &&
    (!productActivityOverTime || productActivityOverTime.length === 0);

  const hasNoAwarenessTrainingData =
    !isLoading &&
    (!productActivityOverTime ||
      productActivityOverTime?.every((r) => Number(r.completedContent) === 0));

  if (hasNoProductActivityData && isMainDashboard) {
    return (
      <ChartEmptyState
        type={ChartEmptyStateType.GENERIC}
        minHeight={510}
        title={t('dashboard.activity.overTime.noActivity')}
        description={t('dashboard.activity.overTime.noActivityDescription')}
      />
    );
  }

  if (hasNoAwarenessTrainingData && !isMainDashboard) {
    return (
      <ChartEmptyState
        type={ChartEmptyStateType.AWT}
        minHeight={510}
        title={t('dashboard.awareness.completions.perMonth.empty.title')}
        description={t(
          'dashboard.awareness.completions.perMonth.empty.description',
        )}
      />
    );
  }

  return (
    <ChartWrapper className={isMainDashboard ? 'mt-4' : ''}>
      <ActivityBarChart
        reportData={productActivityOverTime}
        isLoading={isLoading}
        fromDate={filters.from}
        toDate={filters.to}
        activeAwarenessTraining={activeAwarenessTraining}
        activePhishing={activePhishing}
        activeSecurityCulture={activeSecurityCulture}
        isMainDashboard={isMainDashboard}
      />
    </ChartWrapper>
  );
};
